import { Box, Flex, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { PurchaseButton } from "./PurchaseButton";
import { useState } from "react";
import axios from "axios";
import { TextField } from "../../fields/TextField";
import { ServerResponse } from "../../../types";
import { ImmutableObject } from "@hookstate/core";
import { Release } from "../../../types";
import { FunnelComponentProps } from "../../funnel/types";
import useValidatedAmount from "../../fields/hooks/useValidatedAmount";
import {
  createReleasePdfPurchaseFromConfirmationToken,
  createReleasePdfPurchaseFromSavedPaymentMethod,
} from "../api/paymentApi";
import { EmbeddedStripePaymentForm } from "./EmbeddedStripePaymentForm";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { patchAllQueriesWithCompletedEntity } from "../utils/replaceCompletedEntityInCache";
import { isMatchingPdfReleaseEntity } from "../utils/cacheMatchers";
import { usePaymentCompletion } from "../hooks/usePaymentCompletion";
import { useIsShowingEmbeddedStripeForm } from "../hooks/useIsShowingEmbeddedStripeForm";

interface PdfModalFooterProps {
  release?: ImmutableObject<Release>;
}

export function PdfModalFooter({
  release,
  setFunnelType,
  updateFunnelConfig,
}: FunnelComponentProps & PdfModalFooterProps) {
  if (!release) return null;
  const queryClient = useQueryClient();
  const showEmbeddedForm = useIsShowingEmbeddedStripeForm();
  const {
    display_price,
    is_discounted,
    display_price_discounted,
    allow_variable_max_price,
    price_float,
    price_float_discounted,
  } = release.price;

  const pdfPriceString = is_discounted
    ? display_price_discounted
    : display_price;
  const pdfPriceFloat = is_discounted
    ? (price_float_discounted ?? price_float)
    : price_float;

  const [netAmountError, setNetAmountError] = useState<string>();
  const [variablePrice, setVariablePrice] = useState<number | undefined>(
    pdfPriceFloat,
  );
  const { value, setValue } = useValidatedAmount(
    setVariablePrice,
    pdfPriceFloat.toString(),
  );
  const formatPdfError = (error: Error) => {
    if (axios.isAxiosError<ServerResponse<string>>(error)) {
      const netAmountField = error.response?.data?.payload.form_fields?.find(
        (formField) => formField.name === "netAmount",
      );
      if (netAmountField?.error) {
        // Field-level error: route to the input, suppress the banner.
        setNetAmountError(netAmountField.error);
        return "";
      }
      return (
        error.response?.data?.payload.results ||
        t("components.payments.donate.donationDeclined")
      );
    }
    return t("components.payments.donate.donationDeclined");
  };

  const updateCompletedPdfReleaseCache = async (completedRelease: Release) => {
    patchAllQueriesWithCompletedEntity(queryClient, completedRelease, isMatchingPdfReleaseEntity);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["releaseDetailsRelease"] }),
      queryClient.invalidateQueries({ queryKey: ["comicDetails"] }),
    ]);
  };

  const {
    runAndSwallow: runPdfPurchaseAndSwallow,
    isLoading: waiting,
    error: checkoutError,
  } = usePaymentCompletion<Release>({
    updateCache: updateCompletedPdfReleaseCache,
    onSuccess: (completedRelease) => {
      updateFunnelConfig({
        context: { purchaseRelease: completedRelease },
      });
      setFunnelType(FUNNEL_TYPES.downloadPdf);
    },
    formatError: formatPdfError,
  });

  const handleConfirmationTokenComplete = (confirmationTokenId: string) => {
    runPdfPurchaseAndSwallow(async () => {
      const response = await createReleasePdfPurchaseFromConfirmationToken(
        release.id,
        {
          confirmationTokenId,
          netAmount: variablePrice ?? pdfPriceFloat,
        },
      );
      return response.data.payload.results;
    });
  };

  const handleSavedPaymentMethodClick = () => {
    runPdfPurchaseAndSwallow(async () => {
      const response = await createReleasePdfPurchaseFromSavedPaymentMethod(
        release.id,
        { netAmount: variablePrice ?? pdfPriceFloat },
      );
      return response.data.payload.results;
    });
  };

  const buttonText =
    allow_variable_max_price
      ? t("components.payments.pdf.purchaseRelease")
      : `${t("components.payments.pdf.purchaseReleaseFor")} ${pdfPriceString}`;

  const isDisabled = !!netAmountError || !variablePrice || waiting;

  const renderPurchaseControl = () => {
    if (waiting) {
      return (
        <PurchaseButton
          text="Confirming your purchase…"
          loading
          isDisabled
        />
      );
    }
    if (showEmbeddedForm) {
      return (
        <EmbeddedStripePaymentForm
          submitButtonText={buttonText}
          disabled={isDisabled}
          onComplete={handleConfirmationTokenComplete}
        />
      );
    }

    return (
      <PurchaseButton
        isDisabled={isDisabled}
        text={buttonText}
        loading={waiting}
        onClick={handleSavedPaymentMethodClick}
      />
    );
  };

  return (
    <Flex
      flexDirection="column"
      width="100%"
      gap="24px"
      padding="24px"
    >
      {!!allow_variable_max_price && (
        <>
          <Flex flexDirection="column">
            <Text fontSize="14px" mb={4} color="dune.700">
              {t("components.payments.pdf.enterAmountYouWantToPay", {
                minPrice: pdfPriceString,
              })}
            </Text>
            <TextField
              type="text"
              placeholder=""
              height="48px"
              variant="outline"
              prefix="$"
              inputMode="decimal"
              error={netAmountError}
              value={`${value}`}
              onBlur={(_) => {
                if (
                  !variablePrice ||
                  (variablePrice && variablePrice < pdfPriceFloat)
                ) {
                  return setNetAmountError(
                    `${t("components.payments.pdf.minimumAmount")} ${pdfPriceString} `,
                  );
                } else {
                  setNetAmountError(undefined);
                }
              }}
              onChange={setValue}
            />
          </Flex>
          <Box width="100%" height="1px" background="transparent.white.10" />
        </>
      )}

      <Flex flexDirection="column" gap="8px" width="100%">
        {renderPurchaseControl()}
        {checkoutError ? (
          <Text color="error.300" fontSize="14px" lineHeight="130%">
            {checkoutError}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );
}
